// frontend/src/portal/pages/PortalForgotPassword.jsx
import React from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth, useSignIn } from "@clerk/clerk-react";

import { Lock, ClipboardCheck, Zap, Building2 } from "lucide-react";

function getSafeReturnUrl(search, fallback = "/portal/overview") {
  const params = new URLSearchParams(search);
  const raw = params.get("returnUrl");
  if (!raw) return fallback;

  // Prevent open-redirects: only allow internal portal paths
  return raw.startsWith("/portal/") ? raw : fallback;
}

export default function PortalForgotPassword() {
  const location = useLocation();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();

  const returnUrl = React.useMemo(() => getSafeReturnUrl(location.search, "/portal/overview"), [location.search]);

  // Form state
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  // Flow state
  const [step, setStep] = React.useState("request"); // "request" | "reset"
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [secondFactor, setSecondFactor] = React.useState(false);

  // If already signed in, send them back safely
  React.useEffect(() => {
    if (authLoaded && isSignedIn) {
      window.location.href = returnUrl;
    }
  }, [authLoaded, isSignedIn, returnUrl]);

  const sendResetCode = async (e) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1) Send reset code to email
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });

      setStep("reset");
    } catch (err) {
      console.error("[PortalForgotPassword] sendResetCode error:", err);
      const msg = err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || "Failed to send reset code.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError(null);

    try {
      // 2) Verify code + set new password
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password: newPassword,
      });

      if (result.status === "needs_second_factor") {
        setSecondFactor(true);
        setError(null);
        return;
      }

      if (result.status === "complete") {
        // 3) Activate session & redirect safely
        await setActive({ session: result.createdSessionId });
        window.location.href = returnUrl;
        return;
      }

      // Any other status is unexpected for this flow
      console.warn("[PortalForgotPassword] unexpected status:", result?.status, result);
      setError("Reset not completed. Please try again.");
    } catch (err) {
      console.error("[PortalForgotPassword] resetPassword error:", err);
      const msg = err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || "Failed to reset password.";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render until Clerk is loaded
  if (!isLoaded) {
    return (
      <div className="loading-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .loading-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
          background: #ffffff;
        }

        .loading-container p {
          color: #64748b;
        }

        .forgot-wrapper {
          min-height: 100vh;
          display: flex;
          font-family: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
          background: #ffffff;
        }

        /* Left side - Form */
        .forgot-form-side {
          flex: 1;
          max-width: 100%;
          padding: 32px 24px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          background: #ffffff;
          position: relative;
          z-index: 2;
        }

        .form-container {
          max-width: 440px;
          margin: 0 auto;
          width: 100%;
        }

        .logo-section {
          margin-bottom: 32px;
          animation: slideInLeft 0.6s ease-out;
        }

        .logo-text {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .header-section {
          margin-bottom: 24px;
          animation: slideInLeft 0.6s ease-out 0.1s both;
        }

        .header-title {
          font-size: 26px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px 0;
          letter-spacing: -0.03em;
        }

        .header-subtitle {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          font-weight: 400;
          line-height: 1.5;
        }

        .error-message {
          margin-bottom: 16px;
          padding: 12px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-size: 14px;
          animation: slideInLeft 0.3s ease-out;
        }

        .warning-message {
          margin-bottom: 16px;
          padding: 12px 16px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          border-radius: 8px;
          color: #9a3412;
          font-size: 14px;
          animation: slideInLeft 0.3s ease-out;
        }

        .forgot-form {
          animation: slideInLeft 0.6s ease-out 0.2s both;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #334155;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          font-size: 15px;
          color: #0f172a;
          outline: none;
          transition: all 0.2s ease;
          box-sizing: border-box;
          font-family: inherit;
        }

        .form-input:focus {
          border-color: #0ea5e9;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }

        .form-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .password-wrapper {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          padding: 4px 8px;
        }

        .submit-button {
          width: 100%;
          padding: 14px;
          background: #0ea5e9;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 12px;
        }

        .submit-button:hover:not(:disabled) {
          background: #0284c7;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
        }

        .submit-button:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }

        .form-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .text-button {
          background: none;
          border: none;
          padding: 0;
          color: #0ea5e9;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .text-button:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .text-link {
          font-size: 14px;
          color: #0ea5e9;
          text-decoration: none;
          font-weight: 500;
        }

        /* Right side - Hero */
        .forgot-hero-side {
          flex: 1;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 40px;
          position: relative;
          overflow: hidden;
        }

        .hero-grid {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: fadeIn 1s ease-out;
        }

        .hero-orb-1 {
          position: absolute;
          top: 20%;
          left: 15%;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(14, 165, 233, 0.3) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
          animation: float 8s ease-in-out infinite;
        }

        .hero-orb-2 {
          position: absolute;
          bottom: 15%;
          right: 20%;
          width: 250px;
          height: 250px;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.2) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(50px);
          animation: float 6s ease-in-out infinite reverse;
        }

        .hero-content {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: fadeIn 1s ease-out 0.3s both;
        }

        .hero-title {
          font-size: 42px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 16px 0;
          letter-spacing: -0.03em;
          line-height: 1.2;
        }

        .hero-gradient-text {
          background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 16px;
          color: #94a3b8;
          margin: 0 auto 32px;
          max-width: 480px;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin: 48px auto 0;
          max-width: 600px;
          width: 100%;
        }

        .feature-card {
          background: rgba(14, 165, 233, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(14, 165, 233, 0.1);
          border-radius: 12px;
          padding: 20px 16px;
          text-align: left;
          transition: all 0.3s ease;
        }

        .feature-card:hover {
          background: rgba(14, 165, 233, 0.1);
          border-color: rgba(14, 165, 233, 0.2);
          transform: translateY(-4px);
        }

        .feature-icon {
          margin-bottom: 12px;
          color: #38bdf8;
        }

        .feature-title {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          margin: 0 0 6px 0;
        }

        .feature-desc {
          font-size: 12px;
          color: #94a3b8;
          margin: 0;
          line-height: 1.5;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .forgot-wrapper {
            flex-direction: column;
          }

          .forgot-hero-side {
            display: none;
          }

          .forgot-form-side {
            padding: 24px 20px;
            min-height: 100vh;
          }

          .logo-text {
            font-size: 22px;
          }

          .header-title {
            font-size: 22px;
          }

          .header-subtitle {
            font-size: 13px;
          }

          .logo-section {
            margin-bottom: 24px;
          }

          .header-section {
            margin-bottom: 20px;
          }

          .form-input {
            font-size: 16px; /* Prevents zoom on iOS */
          }

          .form-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .text-button,
          .text-link {
            text-align: center;
          }
        }

        /* Tablet */
        @media (min-width: 769px) and (max-width: 1024px) {
          .forgot-form-side {
            flex: 0 0 400px;
            padding: 40px 32px;
          }

          .hero-title {
            font-size: 36px;
          }

          .hero-subtitle {
            font-size: 15px;
          }

          .features-grid {
            gap: 12px;
          }

          .feature-card {
            padding: 16px 14px;
          }

          .forgot-hero-side {
            padding: 40px 32px;
          }
        }

        /* Desktop */
        @media (min-width: 1025px) {
          .forgot-form-side {
            flex: 0 0 480px;
            padding: 60px 80px;
          }
        }

        /* Small mobile */
        @media (max-width: 375px) {
          .forgot-form-side {
            padding: 20px 16px;
          }

          .header-title {
            font-size: 20px;
          }

          .submit-button {
            padding: 12px;
            font-size: 14px;
          }
        }
      `}</style>

      <div className="forgot-wrapper">
        {/* Left Side - Form */}
        <div className="forgot-form-side">
          <div className="form-container">
            {/* Logo */}
            <div className="logo-section">
              <div className="logo-text">CBCFX</div>
            </div>

            {/* Header */}
            <div className="header-section">
              <h1 className="header-title">{step === "request" ? "Reset your password" : "Set a new password"}</h1>
              <p className="header-subtitle">
                {step === "request" ? "Enter your work email. We'll send a verification code." : "Enter the verification code and choose a new password."}
              </p>
            </div>

            {/* Error Message */}
            {error && <div className="error-message">{error}</div>}

            {secondFactor && <div className="warning-message">Your account requires 2-step verification to complete this reset. Please sign in normally and complete 2FA.</div>}

            {/* Step 1: Request code */}
            {step === "request" && (
              <form onSubmit={sendResetCode} className="forgot-form">
                <div className="form-group">
                  <label className="form-label">Work email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" disabled={isLoading} required className="form-input" />
                </div>

                <button type="submit" disabled={isLoading} className="submit-button">
                  {isLoading ? "Sending code..." : "Send reset code"}
                </button>

                <div className="form-actions">
                  <Link to={`/portal/login?returnUrl=${encodeURIComponent(returnUrl)}`} className="text-link">
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}

            {/* Step 2: Reset password */}
            {step === "reset" && (
              <form onSubmit={resetPassword} className="forgot-form">
                <div className="form-group">
                  <label className="form-label">Verification code</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    disabled={isLoading || secondFactor}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">New password</label>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Choose a strong password"
                      disabled={isLoading || secondFactor}
                      required
                      className="form-input"
                    />
                    <button type="button" onClick={() => setShowPassword((s) => !s)} disabled={isLoading || secondFactor} className="password-toggle">
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={isLoading || secondFactor} className="submit-button">
                  {isLoading ? "Updating password..." : "Reset password"}
                </button>

                <div className="form-actions">
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => {
                      setError(null);
                      setSecondFactor(false);
                      setCode("");
                      setNewPassword("");
                      setStep("request");
                    }}
                    className="text-button">
                    Use a different email
                  </button>

                  <Link to={`/portal/login?returnUrl=${encodeURIComponent(returnUrl)}`} className="text-link">
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Right Side - Hero */}
        <div className="forgot-hero-side">
          {/* Animated grid background */}
          <div className="hero-grid" />

          {/* Glowing orbs */}
          <div className="hero-orb-1" />
          <div className="hero-orb-2" />

          <div className="hero-content">
            <h2 className="hero-title">
              Secure Account Recovery
              <br />
              <span className="hero-gradient-text">Built for compliance.</span>
            </h2>

            <p className="hero-subtitle">Reset your password with a verification code sent to your email.</p>

            <div className="features-grid">
              {[
                {
                  icon: Lock,
                  title: "Secure by default",
                  desc: "Code-based verification, no manual resets.",
                },
                {
                  icon: ClipboardCheck,
                  title: "Audit-friendly",
                  desc: "Self-service flow suitable for regulated environments.",
                },
                {
                  icon: Zap,
                  title: "Fast recovery",
                  desc: "Get back in within minutes.",
                },
                {
                  icon: Building2,
                  title: "Tenant-aware",
                  desc: "Return to the right workspace after sign-in.",
                },
              ].map((feature, i) => (
                <div key={i} className="feature-card">
                  <div className="feature-icon">
                    <feature.icon size={24} strokeWidth={1.8} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-desc">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
